import React, { useMemo, useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { Refresh as RefreshIcon, Download as DownloadIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { getDateInputProps } from '../utils/dateConstraints';
import { api, endpoints } from '../api/config';


const AllScraperItemData = () => {
  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  const [isRefreshing, setIsRefreshing] = useState(false);
  // Pre-layer state
  const [showPreLayer] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState('');
  const [progressData, setProgressData] = useState(null);
  
  // Scrape files state
  const [scrapeFilesData, setScrapeFilesData] = useState(null);
  const [scrapeFilesLoading, setScrapeFilesLoading] = useState(false);
  const [scrapeFilesError, setScrapeFilesError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const allVersionsComplete = useMemo(() => {
    try {
      const coerceNumber = (n) => {
        const x = Number(n);
        return Number.isFinite(x) ? x : 0;
      };

      const versionStatuses = [];

      if (progressData && typeof progressData === 'object' && !Array.isArray(progressData)) {
        Object.values(progressData).forEach((payload) => {
          if (!payload || typeof payload !== 'object') return;
          const total = coerceNumber(payload.total_urls || payload.total || payload.count || 0);
          const versions = Array.isArray(payload.versions) ? payload.versions : [];
          versions.forEach((v) => {
            const completed = coerceNumber(v?.completed_urls || v?.completed || v?.complete_urls || 0);
            const errorCount = coerceNumber(v?.error_count || 0);
            const completeFlag = Boolean(v?.complete);
            const isComplete = completeFlag || (total > 0 && completed + errorCount >= total);
            versionStatuses.push(isComplete);
          });
        });
      } else if (Array.isArray(progressData)) {
        progressData.forEach((item) => {
          const total = coerceNumber(item?.total_urls || item?.total || item?.count || 0);
          const completed = coerceNumber(item?.completed_urls || item?.completed || item?.complete_urls || 0);
          const errorCount = coerceNumber(item?.error_count || 0);
          const completeFlag = Boolean(item?.complete);
          const isComplete = completeFlag || (total > 0 && completed + errorCount >= total);
          versionStatuses.push(isComplete);
        });
      }

      if (versionStatuses.length === 0) return false;
      return versionStatuses.every(Boolean);
    } catch {
      return false;
    }
  }, [progressData]);

  const fetchProgress = useCallback(async () => {
    try {
      setProgressLoading(true);
      setProgressError('');
      const resp = await fetch('https://progresstracker-copy-production.up.railway.app/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: 'progress-admin', password: 'VR6qjCLCxXa4kBbu8aUT' })
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(json?.message || `HTTP ${resp.status}`);
      }
      setProgressData(json);
    } catch (e) {
      setProgressError(String(e?.message || e));
    } finally {
      setProgressLoading(false);
    }
  }, []);

  const fetchScrapeFiles = useCallback(async () => {
    try {
      setScrapeFilesLoading(true);
      setScrapeFilesError('');
      
      const response = await api.get(endpoints.scrapeFiles, {
        params: selectedDate ? { date: selectedDate } : {}
      });
      
      const data = response.data?.data ?? response.data;
      setScrapeFilesData(data);
    } catch (e) {
      setScrapeFilesError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setScrapeFilesLoading(false);
    }
  }, [selectedDate]);


  // Trigger progress endpoint on first render (pre-layer)
  useEffect(() => {
    if (!showPreLayer) return;
    fetchProgress();
    fetchScrapeFiles();
  }, [showPreLayer, fetchProgress, fetchScrapeFiles]);


  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      // Refresh both progress and scrape files data
      await Promise.all([fetchProgress(), fetchScrapeFiles()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const scrapeFilesGridRows = useMemo(() => {
    if (!Array.isArray(scrapeFilesData)) return [];
    return scrapeFilesData.map((file, idx) => ({
      id: file.id ?? `${file.date || 'date'}-${file.type || 'type'}-${file.version || 'version'}-${idx}`,
      ...file,
    }));
  }, [scrapeFilesData]);

  const scrapeFilesGridColumns = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        resizable: true,
        minWidth: 150,
        flex: 1,
        renderCell: (params) => params.row?.date || '-',
      },
      {
        field: 'type',
        headerName: 'Type',
        resizable: true,
        minWidth: 140,
        flex: 1,
        renderCell: (params) =>
          typeof params.row?.type === 'string'
            ? params.row.type.replace(/\blowes\b/gi, "Lowe's")
            : params.row?.type || '-',
      },
      {
        field: 'version',
        headerName: 'Version',
        resizable: true,
        minWidth: 120,
        flex: 0.8,
        renderCell: (params) => (
          <Chip
            label={params.row?.version || '-'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.75rem', height: '24px' }}
          />
        ),
      },
      {
        field: 'download',
        headerName: 'Download',
        minWidth: 160,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row?.file_url ? (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              onClick={() => window.open(params.row.file_url, '_blank')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.open(params.row.file_url, '_blank');
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'primary.main',
                cursor: 'pointer',
                userSelect: 'none',
                textDecoration: 'none',
                textUnderlineOffset: '2px',
                '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
              }}
            >
              <DownloadIcon fontSize="small" />
              Download
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              No file available
            </Typography>
          ),
      },
    ],
    []
  );


  const renderPreLayer = () => (
    <DashboardLayout>
      <Box sx={{ p: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.25rem' }}>
            Scraper Dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button variant="outlined" onClick={fetchProgress} disabled={progressLoading} size="small" sx={{ fontSize: '0.8125rem', py: 0.5, px: 1 }}>Check Status</Button>
          </Box>
        </Box>
        <Paper elevation={2}>
          <Box sx={{ p: 1.25 }}>
            {progressLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>Checking progress...</Typography>
              </Box>
            )}
            {!progressLoading && progressError && (
              <Typography variant="body2" color="error" sx={{ fontSize: '0.8125rem' }}>{progressError}</Typography>
            )}
            {!progressLoading && !progressError && progressData && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1.5, 
                mt: 0,
                alignItems: 'stretch'
              }}>
                {(() => {
                  // Build rows of { title, pct, completed, total }
                  const rows = [];
                  const coerceNumber = (n) => {
                    const x = Number(n);
                    return Number.isFinite(x) ? x : 0;
                  };
                  if (progressData && typeof progressData === 'object' && !Array.isArray(progressData)) {
                    Object.entries(progressData).forEach(([groupName, payload]) => {
                      if (!payload || typeof payload !== 'object') return;
                      const total = coerceNumber(payload.total_urls || payload.total || payload.count || 0);
                      const versions = Array.isArray(payload.versions) ? payload.versions : [];
                      versions.forEach((v) => {
                        const versionNum = v?.version ?? v?.day_version ?? v?.ver ?? '';
                        const completed = coerceNumber(v?.completed_urls || v?.completed || v?.complete_urls || 0);
                        const errorCount = coerceNumber(v?.error_count || 0);
                        // Display percentage by completed only; never show 100% unless complete
                        const rawPct = total > 0 ? Math.floor((completed / total) * 100) : 0;
                        const pct = Math.min(rawPct, (completed + errorCount) >= total ? 100 : Math.min(rawPct, 99));
                        rows.push({ title: `${groupName} v${versionNum}`, pct, completed, total, errorCount });
                      });
                    });
                  } else if (Array.isArray(progressData)) {
                    // Fallback if API returns a flat array with totals per version
                    progressData.forEach((item, idx) => {
                      const groupName = String(item?.name || item?.source || item?.key || 'Group');
                      const total = coerceNumber(item?.total_urls || item?.total || item?.count || 0);
                      const versionNum = item?.version ?? item?.day_version ?? idx;
                      const completed = coerceNumber(item?.completed_urls || item?.completed || item?.complete_urls || 0);
                      const errorCount = coerceNumber(item?.error_count || 0);
                      const rawPct = total > 0 ? Math.floor((completed / total) * 100) : 0;
                      const pct = Math.min(rawPct, (completed + errorCount) >= total ? 100 : Math.min(rawPct, 99));
                      rows.push({ title: `${groupName} v${versionNum}`, pct, completed, total, errorCount });
                    });
                  }
                  return rows.map((r, idx) => (
                    <Box 
                      key={`${r.title}-${idx}`} 
                      sx={{ 
                        width: '280px',
                        display: 'flex'
                      }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        <CardContent sx={{ p: 1.25, flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                            {typeof r.title === 'string' ? r.title.replace(/\blowes\b/gi, "Lowe's") : r.title}
                          </Typography>
                          <Typography variant="h6" sx={{ m: 0, fontSize: '1.125rem' }}>{r.pct}%</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.75rem', mt: 'auto' }}>
                            {r.completed} of {r.total} completed{typeof r.errorCount === 'number' ? ` • ${r.errorCount} errors` : ''}
                          </Typography>
                          {(r.completed >= r.total) && r.errorCount === 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6
                              }}
                            >
                              <CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small" />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  ));
                })()}
                {(!Array.isArray(progressData) && (!progressData || Object.keys(progressData || {}).length === 0)) && (
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>No progress data available.</Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: allVersionsComplete ? 'success.main' : 'info.main', fontSize: '0.75rem' }}>
                {allVersionsComplete ? 'All versions are 100% complete.' : 'Scraping is in progress.'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Scrape Files Table */}
        <Box sx={{ mt: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.125rem' }}>
              Scrape Files
        </Typography>
            <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="date"
                label="Filter by Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={getDateInputProps()}
                sx={{ minWidth: { xs: 140, sm: 160 }, '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' } }}
                size="small"
              />
              {selectedDate && (
                <Button 
                  variant="text" 
                  onClick={() => setSelectedDate('')}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.8125rem', py: 0.5, px: 1 }}
                >
                  Show All
                </Button>
              )}
              <Button 
                variant="outlined" 
                onClick={fetchScrapeFiles} 
                disabled={scrapeFilesLoading}
                startIcon={scrapeFilesLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                size="small"
                sx={{ fontSize: '0.8125rem', py: 0.5, px: 1 }}
              >
                {scrapeFilesLoading ? 'Loading...' : 'Refresh'}
              </Button>
              <GlobalDenseModeToolbar
                denseMode={denseMode}
                denseModeTogglePending={denseModeTogglePending}
                persistDenseMode={persistDenseMode}
              />
            </Box>
          </Box>
          
          <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {scrapeFilesError ? (
              <Box sx={{ p: 1, color: 'error.main', fontSize: '0.8125rem' }}>
                {scrapeFilesError}
              </Box>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <HoverExpandDataGridPro
                rows={scrapeFilesGridRows}
                columns={scrapeFilesGridColumns}
                loading={scrapeFilesLoading}
                disableVirtualization
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                pagination
                paginationMode="client"
                pageSizeOptions={[5, 10, 25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                rowHeight={denseMode ? 36 : 52}
                columnHeaderHeight={denseMode ? 40 : 56}
                sx={{ height: '100%', minHeight: 0 }}
              />
            </Box>
          </Paper>
          </Box>
      </Box>
    </DashboardLayout>
  );

  return renderPreLayer();
};

export default AllScraperItemData;


